import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePreferredGenresContext } from '@/contexts/PreferredGenresContext'
import { useGenres } from '@/hooks/genres'
import type { Genre } from '@/types'

interface PreferredGenresSettingsProps {
  onClose?: () => void
  showSuggestions?: boolean
  isModal?: boolean
}

export function PreferredGenresSettings({
  onClose,
  showSuggestions = true,
  isModal = false,
}: PreferredGenresSettingsProps) {
  const { t } = useTranslation()
  const { data: allGenres, isLoading: isLoadingGenres } = useGenres()
  const { preferredGenres, suggestions, updatePreferredGenres, isUpdating, shouldShowSuggestions } =
    usePreferredGenresContext()

  const [selectedGenreIds, setSelectedGenreIds] = useState<Set<string>>(
    new Set(preferredGenres.map((genre) => genre.id))
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [showingSection, setShowingSection] = useState<'suggestions' | 'all' | 'both'>('all')

  // Filter genres based on search term
  const filteredGenres = useMemo(() => {
    if (!allGenres) return []
    if (!searchTerm.trim()) return allGenres

    return allGenres.filter((genre) => genre.name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [allGenres, searchTerm])

  // Suggestions with selection state
  const suggestionsWithState = useMemo(() => {
    return suggestions.map((genre) => ({
      ...genre,
      isSelected: selectedGenreIds.has(genre.id),
    }))
  }, [suggestions, selectedGenreIds])

  // All genres with selection state and filtered
  const allGenresWithState = useMemo(() => {
    return filteredGenres.map((genre) => ({
      ...genre,
      isSelected: selectedGenreIds.has(genre.id),
    }))
  }, [filteredGenres, selectedGenreIds])

  const handleGenreToggle = (genreId: string) => {
    const newSelection = new Set(selectedGenreIds)
    if (newSelection.has(genreId)) {
      newSelection.delete(genreId)
    } else {
      newSelection.add(genreId)
    }
    setSelectedGenreIds(newSelection)
  }

  const handleSelectSuggestions = () => {
    const suggestionIds = suggestions.map((s) => s.id)
    setSelectedGenreIds(new Set(suggestionIds))
  }

  const handleClearAll = () => {
    setSelectedGenreIds(new Set())
  }

  const handleSave = () => {
    updatePreferredGenres(Array.from(selectedGenreIds))
    onClose?.()
  }

  const hasChanges = useMemo(() => {
    const currentIds = new Set(preferredGenres.map((g) => g.id))
    if (currentIds.size !== selectedGenreIds.size) return true
    return !Array.from(currentIds).every((id) => selectedGenreIds.has(id))
  }, [preferredGenres, selectedGenreIds])

  const selectedCount = selectedGenreIds.size

  if (isLoadingGenres) {
    return (
      <div className={`${isModal ? 'p-6' : ''}`}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${isModal ? 'p-4 lg:p-6' : 'p-4 lg:p-6'} max-w-2xl mx-auto`}>
      {/* Header */}
      <div className="mb-4 lg:mb-6">
        <h2 className="text-xl lg:text-2xl font-bold mb-1 lg:mb-2">
          {t('settings.preferredGenres.title')}
        </h2>
        <p className="text-muted-foreground text-sm lg:text-base">
          {t('settings.preferredGenres.description')}
        </p>
        {selectedCount > 0 && (
          <p className="text-sm text-primary mt-2">
            {t('settings.preferredGenres.selectedCount', { count: selectedCount })}
          </p>
        )}
      </div>

      {/* Quick Actions for Suggestions */}
      {shouldShowSuggestions && showSuggestions && suggestions.length > 0 && (
        <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h3 className="font-semibold text-primary text-sm lg:text-base">
              {t('settings.preferredGenres.smartSuggestions')}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectSuggestions}
              className="text-xs self-start sm:self-auto"
            >
              {t('settings.preferredGenres.selectAll')}
            </Button>
          </div>
          <p className="text-xs lg:text-sm text-muted-foreground mb-3">
            {t('settings.preferredGenres.suggestionsHelp')}
          </p>

          <div className="flex flex-wrap gap-1.5 lg:gap-2">
            {suggestionsWithState.map((genre) => (
              <button
                key={genre.id}
                type="button"
                onClick={() => handleGenreToggle(genre.id)}
                className={`flex items-center gap-1.5 lg:gap-2 px-2.5 lg:px-3 py-1 lg:py-1.5 rounded-full text-xs lg:text-sm border transition-all touch-manipulation ${
                  genre.isSelected
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card hover:border-primary/50 active:border-primary/50'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: genre.color }}
                />
                <span className="truncate max-w-20 lg:max-w-none">{genre.name}</span>
                <span className="text-xs opacity-70 flex-shrink-0">({genre.book_count})</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search and View Toggle */}
      <div className="mb-4 space-y-3">
        <Input
          type="text"
          placeholder={t('settings.preferredGenres.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />

        {shouldShowSuggestions && showSuggestions && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 lg:gap-2">
            <Button
              variant={showingSection === 'suggestions' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowingSection('suggestions')}
              className="text-xs lg:text-sm"
            >
              {t('settings.preferredGenres.suggestions')}
            </Button>
            <Button
              variant={showingSection === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowingSection('all')}
              className="text-xs lg:text-sm"
            >
              {t('settings.preferredGenres.allGenres')}
            </Button>
            <Button
              variant={showingSection === 'both' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowingSection('both')}
              className="text-xs lg:text-sm"
            >
              {t('settings.preferredGenres.both')}
            </Button>
          </div>
        )}
      </div>

      {/* Genres Grid */}
      <div className="space-y-4 lg:space-y-6">
        {/* Suggestions Section */}
        {(showingSection === 'suggestions' || showingSection === 'both') &&
          suggestions.length > 0 && (
            <div>
              <h3 className="font-medium mb-2 lg:mb-3 text-primary text-sm lg:text-base">
                {t('settings.preferredGenres.basedOnLibrary')}
              </h3>
              <div className="grid grid-cols-1 gap-2 lg:gap-3">
                {suggestionsWithState
                  .filter(
                    (genre) =>
                      !searchTerm.trim() ||
                      genre.name.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((genre) => (
                    <GenreSelectionCard
                      key={genre.id}
                      genre={genre}
                      isSelected={genre.isSelected}
                      onToggle={() => handleGenreToggle(genre.id)}
                      showBookCount={true}
                      bookCount={genre.book_count}
                    />
                  ))}
              </div>
            </div>
          )}

        {/* All Genres Section */}
        {(showingSection === 'all' || showingSection === 'both') && (
          <div>
            <div className="flex items-center justify-between mb-2 lg:mb-3">
              <h3 className="font-medium text-sm lg:text-base">
                {showingSection === 'both'
                  ? t('settings.preferredGenres.otherGenres')
                  : t('settings.preferredGenres.allGenres')}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-xs lg:text-sm"
              >
                {t('settings.preferredGenres.clearAll')}
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2 lg:gap-3">
              {allGenresWithState
                .filter(
                  (genre) => showingSection === 'all' || !suggestions.some((s) => s.id === genre.id)
                )
                .map((genre) => (
                  <GenreSelectionCard
                    key={genre.id}
                    genre={genre}
                    isSelected={genre.isSelected}
                    onToggle={() => handleGenreToggle(genre.id)}
                  />
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 lg:mt-8 pt-4 lg:pt-6 border-t">
        <div className="text-xs lg:text-sm text-muted-foreground">
          {selectedCount > 0
            ? t('settings.preferredGenres.selectedSummary', { count: selectedCount })
            : t('settings.preferredGenres.noGenresSelected')}
        </div>

        <div className="flex gap-2 lg:gap-3 self-end sm:self-auto">
          {onClose && (
            <Button variant="outline" onClick={onClose} size="sm" className="text-sm">
              {t('common.cancel')}
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isUpdating}
            size="sm"
            className="text-sm"
          >
            {isUpdating ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface GenreSelectionCardProps {
  genre: Genre & { isSelected?: boolean }
  isSelected: boolean
  onToggle: () => void
  showBookCount?: boolean
  bookCount?: number
}

function GenreSelectionCard({
  genre,
  isSelected,
  onToggle,
  showBookCount = false,
  bookCount,
}: GenreSelectionCardProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-3 lg:gap-4 p-3 lg:p-4 rounded-lg border text-left transition-all touch-manipulation active:scale-[0.98] lg:hover:scale-[1.01] lg:hover:shadow-md ${
        isSelected
          ? 'border-primary bg-primary/10 shadow-sm ring-1 ring-primary/20'
          : 'border-border bg-card hover:border-primary/50 active:border-primary/50'
      }`}
    >
      {/* Left side: Color indicator and content */}
      <div className="flex items-center gap-2.5 lg:gap-3 flex-1 min-w-0">
        <div
          className="w-4 h-4 lg:w-5 lg:h-5 rounded-full flex-shrink-0 ring-1 ring-black/10"
          style={{ backgroundColor: genre.color }}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm lg:text-sm leading-tight">{genre.name}</div>
          {showBookCount && bookCount !== undefined && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {bookCount} {bookCount === 1 ? 'livro' : 'livros'}
            </div>
          )}
        </div>
      </div>

      {/* Right side: Selection indicator */}
      <div className="flex-shrink-0">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-primary border-primary text-primary-foreground scale-110'
              : 'border-border hover:border-primary/60'
          }`}
        >
          {isSelected && (
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>
      </div>
    </button>
  )
}
